import { Big, type BigSource } from "big.js";
import { UnexpectedError } from "../domain/error/errors.ts";

export function Decimal(arg: BigSource): TDecimal {
  try {
    return Big(arg);
  } catch (e) {
    throw new Error(
      `${e instanceof Error ? e.message : String(e)}: with args: Decimal(${arg.toString()}) type: ${typeof arg}`,
      { cause: e },
    );
  }
}

export function isDecimal(x: unknown): x is TDecimal {
  return x instanceof Big;
}

export type TDecimal = InstanceType<typeof Big>;
export type Rate = TDecimal & { __type: "rate" };
export type Percent = TDecimal & { __type: "percent" };

declare const nonZeroBrand: unique symbol;
export type NonZero<T extends TDecimal> = T & { readonly [nonZeroBrand]: true };
export type Zero<T extends TDecimal> = T & { readonly [nonZeroBrand]: false };

export function isZero<T extends TDecimal>(value: T): value is Zero<T> {
  return !isNonZero(value);
}
export function isNonZero<T extends TDecimal>(value: T): value is NonZero<T> {
  return !value.eq(0);
}

export function unsafeNonZero<T extends TDecimal>(value: T): NonZero<T> {
  if (value.eq(0)) {
    throw new UnexpectedError("unsafeNonZero called with zero");
  }
  return value as NonZero<T>;
}

export const ONE_HUNDRED = unsafeNonZero(Decimal(100));

export const DISPLAY_SETTINGS_DECIMAL_PRECISION = 2;

export const addDecimal = <T extends TDecimal>(a: T, b: T): T => a.add(b) as T;
export const sumDecimal = <T extends TDecimal>(input: T[]): T =>
  input.reduce((acc, curr) => addDecimal(acc, curr), Decimal(0) as T);

export const subDecimal = (a: TDecimal, b: TDecimal): TDecimal => a.sub(b);

export function compareNullableDecimal(a: TDecimal | null, b: TDecimal | null): number {
  if (a === null && b === null) {
    return 0;
  }
  if (a === null) {
    return 1;
  }
  if (b === null) {
    return -1;
  }
  return a.cmp(b);
}

export const sumDecimalObjects = <T, K extends DecimalKeyOf<T>>(input: T[], key: K): Extract<T[K], TDecimal> =>
  input.reduce((acc, curr) => acc.add((curr[key] as TDecimal | null) ?? Decimal(0)), Decimal(0)) as Extract<
    T[K],
    TDecimal
  >;

export function mulDecimal<T extends TDecimal>(a: T, b: TDecimal): T {
  return a.mul(b) as T;
}

export const divDecimal = (a: TDecimal, b: NonZero<TDecimal>): Rate => a.div(b) as Rate;

export function divOrDefault({
  numerator,
  divisor,
  defaultValue = Decimal(0),
}: {
  numerator: TDecimal;
  divisor: TDecimal;
  defaultValue?: TDecimal;
}): TDecimal {
  const isNz = isNonZero(divisor);
  return isNz ? divDecimal(numerator, divisor) : defaultValue;
}

const fallbackZero = Decimal(0);
export const asRate = (d: TDecimal | null) => (d ? divDecimal(d, ONE_HUNDRED) : fallbackZero) as Rate;
export const asPercentage = (d: TDecimal, n: TDecimal) => {
  const nz = isNonZero(n);
  return nz ? mulDecimal(divDecimal(d, n), ONE_HUNDRED) : null;
};

export type DecimalKeyOf<T> = keyof {
  [K in keyof T]: T[K] extends TDecimal | null ? K : never;
};

export type DecimalKeysToNumber<T extends object> = {
  [K in keyof T]: T[K] extends TDecimal ? number : T[K] extends TDecimal | null ? number | null : T[K];
};

export function decimalEquals(a: TDecimal | null, b: TDecimal | null): boolean {
  if (a === null && b === null) {
    return true;
  }
  if (a === null || b === null) {
    return false;
  }
  return a.eq(b);
}

export function toFixedNumber(d: TDecimal, precision = 2): number {
  return Number(d.toFixed(precision));
}
